import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, X, FileText, Send, Eye } from 'lucide-react';

const InvoiceSuccessModal = ({ 
  isOpen, 
  onClose, 
  invoiceData, 
  recipientName, 
  recipientEmail,
  onSendAnother 
}) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setCountdown(5); // Reset countdown
    }, 300); // Match animation duration
  };

  useEffect(() => {
    if (isOpen && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isOpen && countdown === 0) {
      handleClose();
    }
  }, [isOpen, countdown, onClose]);

  const handleViewInvoices = () => {
    handleClose();
    setTimeout(() => {
      navigate('/portal/invoices/sent');
    }, 300);
  };

  const handleSendAnother = () => {
    handleClose();
    setTimeout(() => {
      onSendAnother();
    }, 300);
  };

  if (!isOpen) return null;

  const formatCurrency = (amount, currency) => {
    const symbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '';
    return `${symbol}${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" />
      
      {/* Modal */}
      <div 
        className={`relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto transform transition-all duration-300 ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-6 md:p-8">
          {/* Success Icon with Animation */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-green-100 rounded-full animate-ping opacity-75" />
              <div className="relative bg-green-500 rounded-full p-4">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
            </div>
          </div>

          {/* Success Message */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Invoice Sent Successfully!
            </h2>
            <p className="text-gray-600">
              Your invoice has been sent to {recipientName || recipientEmail}
            </p>
          </div>

          {/* Invoice Summary Card */}
          <div className="bg-gray-50 rounded-xl p-5 mb-6 border border-gray-200">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Invoice Number</span>
                <span className="text-sm font-semibold text-gray-900">
                  {invoiceData?.invoiceNumber || 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Amount</span>
                <span className="text-lg font-bold text-gray-900">
                  {formatCurrency(invoiceData?.total, invoiceData?.currency)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Due Date</span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatDate(invoiceData?.dueDate)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Recipient</span>
                <span className="text-sm font-semibold text-gray-900 truncate ml-2 max-w-[200px]">
                  {recipientName || recipientEmail}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleViewInvoices}
              className="w-full flex items-center justify-center px-6 py-3 bg-[#1F11ED] text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-semibold shadow-lg"
            >
              <FileText className="w-5 h-5 mr-2" />
              View Sent Invoices
            </button>
            <button
              onClick={handleSendAnother}
              className="w-full flex items-center justify-center px-6 py-3 bg-white text-[#1F11ED] border-2 border-[#1F11ED] rounded-lg hover:bg-blue-50 transition-colors duration-200 font-semibold"
            >
              <Send className="w-5 h-5 mr-2" />
              Send Another Invoice
            </button>
          </div>

          {/* Auto-dismiss countdown */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              This message will close automatically in {countdown} second{countdown !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceSuccessModal;

